import { PropsWithChildren, ReactNode, cloneElement, forwardRef } from 'react';

import { TACTIC_POSITION_STYLES } from '../../utils/tacticUtils';
import { SponsorsFieldLayout } from '../Team/SponsorsFieldLayout';

import useBetWarriorSegmentation from '@/hooks/useBetWarriorSegmentation';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import {
  EnumPosition,
  Maybe,
  Optional,
  ChangeTacticAction,
  KeysOf,
} from '@/types';
import { EnumTeamContext, PlayerDataComponentInField } from '@/types/team';
import { cn } from '@/utils';

const rowMessageClass = {
  wrapper:
    'absolute left-2/4 -top-[40px] bg-gdt-blue-header h-[35px] rounded-[8px] z-[0] px-[10px] flex items-center -translate-x-1/2 justify-center',
  text: 'uppercase text-white font-semibold text-xs lg:text-sm whitespace-nowrap',
};

type MainWrapper = PropsWithChildren<{
  onConfirmMobile?: ReactNode;
  context?: EnumTeamContext;
}>;

export type ChangeOptions = {
  enablePosition: EnumPosition.DEF | EnumPosition.MID | EnumPosition.FWD | null;
  rowMessage: string;
  action: ChangeTacticAction | null;
  count: number | null;
};

type FieldProps = {
  isMounted?: boolean;
  isLoading?: boolean;
  floatingCard?: ReactNode;
  backDropOpen?: boolean;
  backDropOnClick?: () => void;
  subtitutes: Optional<JSX.Element[]>;
  goalkeeper: Optional<Maybe<JSX.Element>>;
  defenders: Optional<JSX.Element[]>;
  midfielders: Optional<JSX.Element[]>;
  forwards: Optional<JSX.Element[]>;
  tacticId: number;
  changeTacticOptions?: ChangeOptions;
  selectedPositionOrden?: number;
  context?: EnumTeamContext;
};

type SidebarProps = PropsWithChildren<{
  disabled?: boolean;
}>;

const SideBarComponent = ({ children, disabled }: SidebarProps) => {
  return (
    <aside className="flex flex-col flex-1 min-w-[366px] overflow-x-hidden shadow-lg relative">
      {disabled && (
        <div className="absolute top-0 left-0 w-full h-full bg-black z-[1] opacity-80" />
      )}
      <section className="flex flex-col flex-1 bg-gdt-gray-7 relative">
        {children}
      </section>
    </aside>
  );
};

const MainWrapperComponent = ({
  children,
  onConfirmMobile,
  context,
}: MainWrapper) => {
  const { isMobile } = useIsDesktop();

  return (
    <section className="w-full flex flex-col lg:block text-center">
      <div
        className={cn(
          'lg:bg-[#70BA64] relative flex-1 lg:h-full flex flex-col items-center lg:block',
          {
            'overflow-hidden': context === EnumTeamContext.Build11,
          }
        )}
      >
        {children}
        {isMobile && <div className="mt-auto">{onConfirmMobile}</div>}
      </div>
    </section>
  );
};

const InnerWrapperComponent = ({ children }: PropsWithChildren) => {
  return (
    <div className="bg-[#70BA64] lg:bg-transparent p-3 lg:p-0 inline-block lg:block rounded-lg lg:mt-0">
      {children}
    </div>
  );
};

// eslint-disable-next-line react/display-name
const FieldComponent = forwardRef<HTMLDivElement, FieldProps>(
  (
    {
      floatingCard,
      isMounted,
      isLoading,
      context,
      subtitutes,
      goalkeeper,
      backDropOpen,
      backDropOnClick,
      tacticId,
      defenders,
      forwards,
      midfielders,
      changeTacticOptions,
      selectedPositionOrden,
    },
    ref
  ) => {
    const selectedTacticId = tacticId as KeysOf<typeof TACTIC_POSITION_STYLES>;
    const isEnableForActions =
      changeTacticOptions && changeTacticOptions.enablePosition;
    const isAddAction = Boolean(
      changeTacticOptions &&
        changeTacticOptions.action === ChangeTacticAction.ADD
    );

    const isChangeAction = Boolean(
      changeTacticOptions &&
        changeTacticOptions.action === ChangeTacticAction.CHANGE
    );

    const { isEligible } = useBetWarriorSegmentation();

    const isRowDisabled = (
      rowPosition: EnumPosition,
      hasPlayerSelected?: boolean
    ) => {
      if (isEnableForActions) {
        if (rowPosition === changeTacticOptions.enablePosition) {
          if (isAddAction && hasPlayerSelected) {
            return true;
          }

          return false;
        }

        return true;
      }

      return false;
    };

    return (
      <div
        className={cn('lg:w-auto relative max-w-[62.5rem] w-[18.75rem]', {
          'bg-fieldTeam lg:min-h-[50.5rem]':
            context === EnumTeamContext.Build11,
          'bg-fieldTeam11 lg:min-h-[45.40rem] max-w-100%':
            context === EnumTeamContext.Team11,
          'segmented lg:min-h-[44rem]': isEligible,
        })}
      >
        {/* sponsors cancha */}
        {isMounted && !isEligible && <SponsorsFieldLayout />}
        <div
          ref={ref}
          className={cn(
            'lg:max-w-[32.43rem] max-w-[18.75rem] m-auto flex flex-col relative lg:top-[100px] lg:-translate-x-1 lg:z-[2]',
            {
              'lg:top-[143px]': context === EnumTeamContext.Team11,
            }
          )}
        >
          {backDropOpen && (
            <div
              className={cn('backdrop open', {
                team11: context === EnumTeamContext.Team11,
              })}
              onClick={backDropOnClick}
            />
          )}
          <div className="wrapperPlayers h-[24.75rem] lg:h-[27rem] w-full">
            {!isLoading && (
              <>
                <div
                  className={cn('m-auto lg:mt-1 flex justify-center', {
                    'lg:-mt-3': isEligible,
                  })}
                >
                  {goalkeeper
                    ? cloneElement(goalkeeper, {
                        disabled: isEnableForActions,
                        isSelected:
                          selectedPositionOrden !== undefined &&
                          (selectedPositionOrden === 0 ||
                            selectedPositionOrden === goalkeeper.props.data.id),
                      })
                    : null}
                </div>
                <div
                  className={cn('relative', {
                    'lg:-mt-3': isEligible,
                  })}
                >
                  {!isRowDisabled(EnumPosition.DEF) &&
                    changeTacticOptions?.rowMessage && (
                      <div className={rowMessageClass.wrapper}>
                        <p className={rowMessageClass.text}>
                          {changeTacticOptions.rowMessage}
                        </p>
                      </div>
                    )}
                  <div className={TACTIC_POSITION_STYLES[selectedTacticId].DEF}>
                    {defenders?.map((player, index) => {
                      const playerData =
                        player?.props as PlayerDataComponentInField;
                      const hasPlayerInPosition = Boolean(playerData?.data?.id);

                      const isSelected =
                        selectedPositionOrden !== undefined &&
                        (selectedPositionOrden === playerData?.data?.orden ||
                          selectedPositionOrden === playerData?.data?.id);

                      return cloneElement(player, {
                        key: `def-${index}`,
                        disabled: isRowDisabled(
                          EnumPosition.DEF,
                          hasPlayerInPosition
                        ),
                        displayDeleteIcon:
                          isEnableForActions &&
                          !isAddAction &&
                          !isChangeAction &&
                          !isRowDisabled(EnumPosition.DEF),
                        isSelected,
                      });
                    })}
                  </div>
                </div>
                <div className={cn('relative', { 'lg:-mt-3': isEligible })}>
                  {!isRowDisabled(EnumPosition.MID) &&
                    changeTacticOptions?.rowMessage && (
                      <div className={rowMessageClass.wrapper}>
                        <p className={rowMessageClass.text}>
                          {changeTacticOptions.rowMessage}
                        </p>
                      </div>
                    )}
                  <div className={TACTIC_POSITION_STYLES[selectedTacticId].VOL}>
                    {midfielders?.map((player, index) => {
                      const playerData =
                        player?.props as PlayerDataComponentInField;
                      const hasPlayerInPosition = Boolean(playerData?.data?.id);

                      const isSelected =
                        selectedPositionOrden !== undefined &&
                        (selectedPositionOrden === playerData?.data?.orden ||
                          selectedPositionOrden === playerData?.data?.id);

                      return cloneElement(player, {
                        key: `vol-${index}`,
                        disabled: isRowDisabled(
                          EnumPosition.MID,
                          hasPlayerInPosition
                        ),
                        displayDeleteIcon:
                          isEnableForActions &&
                          !isAddAction &&
                          !isChangeAction &&
                          !isRowDisabled(EnumPosition.MID),
                        isSelected,
                      });
                    })}
                  </div>
                </div>
                <div className={cn('relative', { 'lg:-mt-3': isEligible })}>
                  {!isRowDisabled(EnumPosition.FWD) &&
                    changeTacticOptions?.rowMessage && (
                      <div className={rowMessageClass.wrapper}>
                        <p className={rowMessageClass.text}>
                          {changeTacticOptions.rowMessage}
                        </p>
                      </div>
                    )}
                  <div className={TACTIC_POSITION_STYLES[selectedTacticId].DEL}>
                    {forwards?.map((player, index) => {
                      const playerData =
                        player?.props as PlayerDataComponentInField;
                      const hasPlayerInPosition = Boolean(playerData?.data?.id);

                      const isSelected =
                        selectedPositionOrden !== undefined &&
                        (selectedPositionOrden === playerData?.data?.orden ||
                          selectedPositionOrden === playerData?.data?.id);

                      return cloneElement(player, {
                        key: `del-${index}`,
                        disabled: isRowDisabled(
                          EnumPosition.FWD,
                          hasPlayerInPosition
                        ),
                        displayDeleteIcon:
                          isEnableForActions &&
                          !isAddAction &&
                          !isChangeAction &&
                          !isRowDisabled(EnumPosition.FWD),
                        isSelected,
                      });
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
          {!isEligible && (
            <p className="text-white text-center font-medium text-3xs lg:text-sm mt-3 lg:mt-[0]">
              Suplentes
            </p>
          )}
          {!isLoading && (
            <div
              className={cn(
                'flex justify-between lg:max-w-[24rem] lg:w-full m-auto mt-[-.5rem] lg:mt-[-0.25rem]',
                {
                  'lg:mt-[-23px] lg:max-w-[394px] lg:ml-[69px]': isEligible,
                }
              )}
            >
              {subtitutes?.map((player, index) => {
                const playerData = player?.props as PlayerDataComponentInField;
                const isSelected =
                  (selectedPositionOrden !== undefined &&
                    (selectedPositionOrden === playerData?.data?.orden ||
                      selectedPositionOrden === playerData?.data?.id)) ||
                  (changeTacticOptions?.count === playerData?.data?.id &&
                    context !== EnumTeamContext.Build11);

                return (
                  <div
                    key={`sub-${index}`}
                    className={cn({ 'flex-1 flex justify-center': isEligible })}
                  >
                    {cloneElement(player, {
                      key: `sub-${index}`,
                      disabled:
                        isEnableForActions && !isSelected && isChangeAction,
                      isSelected,
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {isMounted && floatingCard}
      </div>
    );
  }
);

export const FieldLayout = {
  Sidebar: SideBarComponent,
  Main: MainWrapperComponent,
  Inner: InnerWrapperComponent,
  Field: FieldComponent,
};
